import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CCProvider, useCCContext } from '../CC/CCContext';
import { CC, ccConfig, HierarchicalFacetValue } from '../CC/CC'; 
import { generateQuery } from '../CC/utils';
import * as ccUtils from '../CC/utils';
import GenericIcon from '../GenericComponents/GenericIcon';
import GenericCheckbox from '../GenericComponents/GenericCheckbox';
import styles from './TreeView.module.css'; // Import the CSS module
import genericStyles from '../GenericComponents/GenericStyles.module.css';
import HierarchicalFacetValueBrowser from './HierarchicalFacetValueBrowser';

type Node = {
  CN: string;
  term: string;
  count: number;
  hasChildren: boolean;
  childNodes?: Node[];
};

interface HierarchicalFacetProps {
  hierarchicalFacetName: string;
  topCN: string;
  CN?: string;
  contentHeight: string;
  sort?: 'alpha' | 'count';
  onSelect?: (values: string[]) => void;
  showSelectedAbove?: boolean;
}

const TreeView: React.FC<HierarchicalFacetProps> = (props) => {
  const { t } = useTranslation();
  //console.log('Props received by TreeView:', props);
  const { user, cc, setCc, first, setFirst, recordsPerPage, setRecordsPerPage, viewMode, setViewMode, browseMode, setBrowseMode, data, setData } = useCCContext();
  const instanceRef = useRef({});
  const { hierarchicalFacetName, topCN, CN, contentHeight, showSelectedAbove } = props;
  const [nodes, setNodes] = useState<Node[]>([]);
  const [expandedNodes, setExpandedNodes] = useState<string[]>([]); // New state to track expanded nodes
  const [existingQuery, setExistingQuery] = useState<string>("");
  const ctx = useCCContext();
  const [isHierarchicalModalOpen, setIsHierarchicalModalOpen] = useState(false);

  const getChildNodes = async (parentCN: string): Promise<Node[]> => {
    const query = generateQuery(ctx.cc, user, { excludeFacet: hierarchicalFacetName });
    const queryCN = parentCN;
    console.log(query);

    try {
      const data = await ccUtils.postWithCache(process.env.REACT_APP_BASE_URL as string, {
        action: "get",
        command: "facets",
        //fields: `/Record/mqb_thesterm/TermPathPart/CN:1-200:regexp(^${queryCN}..?.?.?$)`,
        fields: `/record/ThesTerm/TermPathPart/CN:1-200:regexp(^${queryCN}..?.?.?$)`,
        query: query,
        sort: "alpha",
        responseformat: "json"
      }, instanceRef);
  
      const values = data?.facets?.facet?.value ? (Array.isArray(data?.facets?.facet?.value) ? data?.facets?.facet?.value : [data?.facets?.facet?.value] ) : [];
      const childNodes: Node[] = values.map((facetValue: any) => ({
        CN: facetValue.value,           // Use CN from the fetched data
        term: facetValue.value,         // For now, use CN as term as well
        count: parseInt(facetValue.count, 10),
        hasChildren: true               // Default to true
      }));

      if(childNodes.length>0) {
        const concatenatedValues = childNodes.map(item => `[${item.CN}]`).join('|');

        //console.log(concatenatedValues);
    
        //console.log('terms for '+queryCN, concatenatedValues);
        const termData = await ccUtils.postWithCache(process.env.REACT_APP_BASE_URL as string, {
          action: "get",
          command: "search",
          query: `/Thes/CN=${concatenatedValues}`,
          range: "1-200",
          fields: "*",
          responseformat: "json"
        }, instanceRef);

        //console.log(termData);
        // Extract terms from the fetched data
        const records = termData?.records?.record ? (Array.isArray(termData?.records?.record) ? termData?.records?.record : [termData?.records?.record]) : [];
       
        const fetchedTerms = records.map((record: any) => record.data.Thes);
        
        // Enrich childNodes with the fetched term data
        const enrichedChildNodes = childNodes.map((node) => {
          const termInfo = fetchedTerms.find((term: any) => term.CN === node.CN);
          if (termInfo) {
            return {
              ...node,
              term: termInfo.Term
            };
          }
          return node;
        });
        
        return enrichedChildNodes;
    } else {
      return [];
    }
  
    } catch (error) {
      console.error("Error fetching child nodes:", error);
      return [];  // Return an empty array in case of error
    }
  };
  
  const updateNodes = async (existingNodes: Node[], currentCN: string | undefined): Promise<Node[]> => {
    const newChildNodes = await getChildNodes(currentCN || "");
  
    const updatedNodes: Node[] = [];
  
    newChildNodes.forEach(newNode => {
      const existingNode = existingNodes.find(n => n.CN === newNode.CN);
      if (existingNode) {
        // Update existing node
        updatedNodes.push({ ...existingNode, count: newNode.count });
      } else {
        // Add new node
        updatedNodes.push(newNode);
      }
    });
  
    // Remove nodes that don't exist in newChildNodes
    const nodesToRemove = existingNodes.filter(
      oldNode => !newChildNodes.some(newNode => newNode.CN === oldNode.CN)
    );
  
    return updatedNodes.filter(node => !nodesToRemove.includes(node));
  };
  
  const initializeExpandedNodes = async (CN: string, nodesList: Node[]) => {
    if (ctx.cc.hierarchicalFacets[hierarchicalFacetName]) {
      const hierarchicalFacetCNs = ctx.cc.hierarchicalFacets[hierarchicalFacetName].map(hf => hf.CN);
      const toExpand = nodesList
        .map(node => node.CN)
        .filter(nodeCN => 
          hierarchicalFacetCNs.some(hfCN => hfCN.startsWith(nodeCN))
        );
  
      const updatedNodesList = [...nodesList];
      for (const nodeCN of toExpand) {
        const nodeIndex = updatedNodesList.findIndex(node => node.CN === nodeCN);
        if (nodeIndex !== -1) {
          updatedNodesList[nodeIndex].childNodes = await getChildNodes(nodeCN);
        }
      }
      
      setExpandedNodes(toExpand);
      return updatedNodesList; // Return the updated list
    }
    return nodesList; // Return the original list if no updates were done
  };
 
  useEffect(() => {
    const query = generateQuery(ctx.cc, user, { excludeFacet: hierarchicalFacetName });
    if(existingQuery!=query) {
      setExistingQuery(query);
      setExpandedNodes([]);
      setNodes([]);
        
      const fetchNodes = async () => {
          let cnToFetch = CN;
          if (!cnToFetch && hierarchicalFacetName) {
            cnToFetch = ccConfig.hierarchicalFacets[hierarchicalFacetName]?.CN;
          }
          if (cnToFetch) {
            const childNodes = await getChildNodes(cnToFetch);
            const updatedChildNodes = await initializeExpandedNodes(cnToFetch, childNodes);
            setNodes(updatedChildNodes);
          }
        };
        
        fetchNodes();
      }
  }, [ctx.cc, ctx.cc.hierarchicalFacets, CN, hierarchicalFacetName]);

  // Function to open the HierarchicalFacetValueBrowser modal
  const openHierarchicalModal = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    setIsHierarchicalModalOpen(true);
  };

  // Function to close the HierarchicalFacetValueBrowser modal
  const closeHierarchicalModal = () => {
    setIsHierarchicalModalOpen(false);
  };

  useEffect(() => {
    const query = generateQuery(ctx.cc, user, { excludeFacet: hierarchicalFacetName });
    if(existingQuery!=query) {
      setExistingQuery(query);
      const localCN = CN ?? ccConfig.hierarchicalFacets[hierarchicalFacetName]?.CN;
      console.log('CC changed', localCN);

      updateNodes(nodes, localCN).then(updatedNodes => {
        setNodes(updatedNodes);
      });
    }
  }, [ctx.cc]);

  const handleNodeToggle = async (node: Node) => {
    if (expandedNodes.includes(node.CN)) {
      // Collapse node
      setExpandedNodes(prev => prev.filter(cn => cn !== node.CN));
    } else {
      // Expand node
      if (!node.childNodes) {
        const childNodes = await getChildNodes(node.CN);
        node.childNodes = childNodes;
      }
      setExpandedNodes(prev => [...prev, node.CN]);
    }
  };

  const handleCheckboxChange = (checked: boolean, value: string) => {
    const termValue = nodes.find(node => node.CN === value)?.term || "";

    if (viewMode == "homepage") {
      setViewMode('list');
    }
    
    if (checked) {
      // If the checkbox is checked, add the CN and term to the list of selected hierarchical facets
      const updatedHierarchicalFacets = ctx.cc.hierarchicalFacets[hierarchicalFacetName]
        ? [...ctx.cc.hierarchicalFacets[hierarchicalFacetName], { CN: value, term: termValue }]
        : [{ CN: value, term: termValue }];

      ctx.setCc((prev: typeof ctx.cc) => ({
        ...prev,
        hierarchicalFacets: {
          ...prev.hierarchicalFacets,
          [hierarchicalFacetName]: updatedHierarchicalFacets
        }
      }));
    } else {
      // If the checkbox is unchecked, remove the CN from the list of selected hierarchical facets
      const updatedHierarchicalFacets = ctx.cc.hierarchicalFacets[hierarchicalFacetName].filter((hf) => hf.CN !== value);

      ctx.setCc((prev: typeof ctx.cc) => ({
        ...prev,
        hierarchicalFacets: {
          ...prev.hierarchicalFacets,
          [hierarchicalFacetName]: updatedHierarchicalFacets
        }
      }));
    }
  };

  const expandNodeByCN = async (cn: string) => {
    const childNodes = await getChildNodes(cn);
    setNodes(prevNodes => {
        // Find the node in the existing nodes
        const nodeIndex = prevNodes.findIndex(node => node.CN === cn);
        if (nodeIndex !== -1) {
            // Replace the node with the new data (including childNodes)
            const updatedNode = {
                ...prevNodes[nodeIndex],
                childNodes: childNodes
            };
            return [
                ...prevNodes.slice(0, nodeIndex),
                updatedNode,
                ...prevNodes.slice(nodeIndex + 1)
            ];
        }
        return prevNodes;
    });
    setExpandedNodes(prev => [...prev, cn]);
};

const handleSelectedValues = async (selectedCNs: string[]) => {
  setExpandedNodes([]);
  for (const cn of selectedCNs) {
      await expandNodeHierarchy(cn, nodes);
  }
};

const expandNodeHierarchy = async (cn: string, currentNodes: Node[]) => {
  // Find the parent node in currentNodes that matches the beginning of the CN
  const parentNode = currentNodes.find(node => cn.startsWith(node.CN));

  if (parentNode) {
      // If the node is not already expanded, fetch its child nodes
      if (!expandedNodes.includes(parentNode.CN)) {
          const childNodes = await getChildNodes(parentNode.CN);
          parentNode.childNodes = childNodes;

          // Update the expandedNodes state
          setExpandedNodes(prev => [...prev, parentNode.CN]);
      }

      // If parentNode has child nodes, and the CN suggests deeper nesting, then recurse
      if (parentNode.childNodes && cn !== parentNode.CN) {
          await expandNodeHierarchy(cn, parentNode.childNodes);
      }
  }
};

  // Calculate the indentation based on the depth of the node in the tree
  const depth = CN?.split('.').length || 1;
  const indentation = (depth - 1) * 10;

  return (
    
    <div>
      { !CN && (
          <div className={styles.searchContainer}>
            <button 
              className={`${genericStyles.GenericButton} ${styles.advancedSearchButton}`} 
              onClick={openHierarchicalModal}
            >
              {t("hierarchical_facet_search")}
            </button>

            {/* HierarchicalFacetValueBrowser modal */}
            {isHierarchicalModalOpen && (
                <HierarchicalFacetValueBrowser 
                  hierarchicalFacetName={hierarchicalFacetName}
                  topCN={topCN}
                  onClose={closeHierarchicalModal}
                  isOpen={true}
                  onSelect={handleSelectedValues}
                />

            )}
          </div>
      )}
      
      {true && showSelectedAbove && (
              <div>
                Selected Values:
                {/* Render selected values here */}
              </div>
      )}

      {nodes.map((node, index) => (
        <>
          <div key={"N_"+index} style={{ marginLeft: `${indentation}px` }} className={styles.nodeContainer}>
            {node.hasChildren ? (
              <span 
                onClick={() => handleNodeToggle(node)} 
                className={styles.toggleIcon}
              >
                {expandedNodes.includes(node.CN) ? (
                  <div className={styles.expandButton}>
                    <GenericIcon icon="regular/minus.svg" />
                  </div>
                ) : (
                  <div className={styles.expandButton}>
                    <GenericIcon icon="regular/plus.svg" />
                  </div>
                )}
              </span>
            ) : (
              <div className={styles.expandButton}></div>
            )
            }
            <GenericCheckbox
              id={`hierarchicalfacet_${CN}_${node.CN}`}
              name={`hierarchicalfacet_${CN}_${node.CN}`}
              value={node.CN}
              checked={
                ctx.cc.hierarchicalFacets[hierarchicalFacetName]
                  ? ctx.cc.hierarchicalFacets[hierarchicalFacetName].some((hf) => hf.CN === node.CN)
                  : false
              }
              onChange={handleCheckboxChange}
              label={`${node.term} (${node.count})`}
            />
          </div>
          {expandedNodes.includes(node.CN) && node.childNodes && (
            <TreeView {...props} key={"TV_"+index} CN={node.CN} />
          )}
        </>
      ))}
    </div>
  );

};

export default TreeView;
